import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageCircle, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";

export default function ContactPage() {
  const { user } = useAuth();
  const [f, setF] = useState({
    name: user?.name || "",
    email: user?.email || "",
    subject: "",
    message: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (f.name.trim().length < 2) return toast.error("Enter your name");
    if (!f.email.includes("@")) return toast.error("Enter a valid email");
    if (f.subject.trim().length < 3) return toast.error("Enter a subject");
    if (f.message.trim().length < 10) return toast.error("Message too short");
    setSaving(true);
    try {
      await api.post("/contact", f);
      toast.success("Message received — we'll get back within 24h.");
      setF({ name: f.name, email: f.email, subject: "", message: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
      <SEO title="Contact us" description="Questions about your Noxi account, dispute, verification or partnership? Get in touch — a human replies within 24 hours."/>
      <div className="overline text-primary mb-2">◆ CONTACT US</div>
      <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-6">Say hello.</h1>
      <p className="text-muted-foreground max-w-2xl mb-10">Questions about your account, dispute status, verifications, partnerships, or press? Drop us a note — a human replies within 24h.</p>

      <div className="grid md:grid-cols-[1fr_320px] gap-8">
        <div className="border border-foreground/15 p-6 bg-card">
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="overline block mb-2">◆ Your name</label>
              <Input data-testid="contact-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none border-foreground/20"/>
            </div>
            <div>
              <label className="overline block mb-2">◆ Email</label>
              <Input data-testid="contact-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-none border-foreground/20"/>
            </div>
          </div>
          <div className="mb-4">
            <label className="overline block mb-2">◆ Subject</label>
            <Input data-testid="contact-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="What's this about?" className="rounded-none border-foreground/20"/>
          </div>
          <div>
            <label className="overline block mb-2">◆ Message</label>
            <Textarea data-testid="contact-message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={7} placeholder="Tell us everything…" className="rounded-none border-foreground/20"/>
          </div>
          <div className="flex justify-end mt-5">
            <button data-testid="contact-submit" onClick={submit} disabled={saving} className="bg-primary text-primary-foreground px-6 py-3 mono text-xs hover:bg-primary/90 disabled:opacity-40">
              {saving ? "SENDING…" : "SEND MESSAGE"}
            </button>
          </div>
        </div>

        <aside className="space-y-4">
          <Card icon={<Mail className="w-4 h-4"/>} title="Email">
            <a href="mailto:noxiiservices@gmail.com" className="hover:text-primary mono text-sm">noxiiservices@gmail.com</a>
          </Card>
          <Card icon={<MessageCircle className="w-4 h-4"/>} title="Support hours">
            <span className="text-sm">Mon – Sat · 09:00 – 21:00 IST</span>
          </Card>
          <Card icon={<ShieldCheck className="w-4 h-4"/>} title="Disputes">
            <span className="text-sm">Have an order dispute? Open one from your order page — an admin joins the chat within hours.</span>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="border border-foreground/15 bg-card p-4">
      <div className="overline mb-2 flex items-center gap-2">{icon} ◆ {title}</div>
      <div className="text-foreground/85">{children}</div>
    </div>
  );
}
